
import { Component, Vue } from "vue-property-decorator";
import { v4 as uuidv4 } from "uuid";
import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { homeBreadcrumbs } from "@/utils/breadcrumbs";
import * as Model from "@gigalot/data-models";

interface CategoryOption {
  text: string;
  value: string;
}

const NOTE_SYSTEM = "feeder-app";
const LEGACY_NOTE_CATEGORY = "daily-note";
const NOTE_CATEGORIES = ["Weather", "Ration", "Kraals", "Equipment", "Breakdowns", "Personnel"];

@Component({
  components: {
    BreadcrumbTrail,
  },
})
export default class NoteReminders extends Vue {
  loading = false;
  saving = false;
  deleting = false;
  snack = false;
  snackText = "";
  errorText = "";
  search = "";
  categoryFilter = "";
  selectedCategory = NOTE_CATEGORIES[0];
  selectedNoteGuid = "";
  noteText = "";
  remind = false;
  dateMenu = false;
  deleteDialog = false;
  noteToDelete: Model.NoteReminder | null = null;
  selectedDateValue = Date.now();

  headers = [
    { text: "Date", value: "time", width: 130 },
    { text: "Category", value: "category", width: 150 },
    { text: "Remind", value: "remind", width: 110 },
    { text: "Note", value: "note" },
    { text: "", value: "actions", width: 80, sortable: false, align: "end" },
  ];

  noteReminders: Model.NoteReminder[] = [];

  async mounted() {
    this.selectedDateValue = this.startOfDay(Date.now());
    await this.loadNotes();
    this.resetEditor();
  }

  get themeClass(): string {
    return this.$store.state.lightDarkMode;
  }

  get moment() {
    return this.$store.state.moment;
  }

  get breadcrumbs() {
    return homeBreadcrumbs({ text: "Notes", disabled: true });
  }

  get selectedDateInput(): string {
    return this.moment(this.selectedDateValue).format("YYYY-MM-DD");
  }

  get categoryOptions(): CategoryOption[] {
    const categories = new Set<string>(NOTE_CATEGORIES);
    this.filteredSystemNotes.forEach((item) => {
      const category = (item.category || "").trim();
      if (category) categories.add(category);
    });

    return Array.from(categories).map((value) => ({
      text: this.formatCategory(value),
      value,
    }));
  }

  get filteredNotes(): Model.NoteReminder[] {
    let notes = this.filteredSystemNotes;

    if (this.categoryFilter) {
      notes = notes.filter((item) => item.category === this.categoryFilter);
    }

    const search = this.search.trim().toLowerCase();
    if (!search) return notes;

    return notes.filter((item) => {
      const haystack = `${this.formatDate(item.time)} ${this.formatCategory(item.category)} ${item.note}`.toLowerCase();
      return haystack.includes(search);
    });
  }

  get filteredSystemNotes(): Model.NoteReminder[] {
    return [...this.noteReminders]
      .filter((item) => item.system === NOTE_SYSTEM)
      .sort((left, right) => (right.time || 0) - (left.time || 0));
  }

  get selectedNote(): Model.NoteReminder | undefined {
    return this.filteredSystemNotes.find((item) => item.guid === this.selectedNoteGuid);
  }

  get canSave(): boolean {
    return this.noteText.trim().length > 0 && !this.loading && !this.saving && !this.deleting;
  }

  setSelectedDate(value: string) {
    this.selectedDateValue = this.startOfDay(this.moment(value).valueOf());
    this.dateMenu = false;
  }

  resetEditor() {
    this.selectedNoteGuid = "";
    this.noteText = "";
    this.remind = false;
    this.ensureSelectedCategory();
  }

  syncEditorFromSelectedNote() {
    const note = this.selectedNote;
    if (!note) {
      this.resetEditor();
      return;
    }

    this.selectedDateValue = this.startOfDay(note.time);
    this.noteText = note?.note || "";
    this.remind = Boolean(note?.remind);
    this.selectedCategory = note?.category || this.defaultCategory();
  }

  startOfDay(value?: number): number {
    if (!value) return this.moment().startOf("day").valueOf();
    return this.moment(value).startOf("day").valueOf();
  }

  formatDate(value?: number): string {
    if (!value) return "";
    return this.moment(value).format("DD MMM YYYY");
  }

  formatCategory(value?: string): string {
    const category = (value || "").trim();
    if (!category) return "";
    return category.replace(/[-_]+/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());
  }

  previewNote(value: string): string {
    const trimmed = (value || "").trim();
    if (trimmed.length <= 120) return trimmed;
    return `${trimmed.slice(0, 117)}...`;
  }

  getNoteRowClass(item: Model.NoteReminder) {
    return item?.guid === this.selectedNoteGuid ? "note-reminders-row--selected" : "";
  }

  selectNote(item: Model.NoteReminder) {
    this.selectedNoteGuid = item.guid;
    this.syncEditorFromSelectedNote();
  }

  confirmDeleteSelectedNote() {
    if (this.selectedNote) this.confirmDeleteNote(this.selectedNote);
  }

  confirmDeleteNote(note: Model.NoteReminder) {
    this.noteToDelete = note;
    this.deleteDialog = true;
  }

  closeDeleteDialog() {
    if (this.deleting) return;

    this.deleteDialog = false;
    this.noteToDelete = null;
  }

  defaultCategory(): string {
    return NOTE_CATEGORIES[0];
  }

  ensureSelectedCategory() {
    if (!this.selectedCategory || this.selectedCategory === LEGACY_NOTE_CATEGORY) {
      this.selectedCategory = this.defaultCategory();
    }
  }

  async loadNotes() {
    this.loading = true;
    this.errorText = "";

    try {
      const gql = `query NoteReminders($guid: String!) {
        NoteReminders(guid: $guid) {
          guid
          changeTracking {
            current {
              guid
              hash
            }
            previous {
              guid
              hash
            }
            serverSaveTime
            save {
              uid
              time
            }
          }
          time
          system
          category
          remind
          note
        }
      }`;

      const response = await this.$store.dispatch("graphQl", {
        gql,
        variables: { guid: this.$store.state.user.location.guid },
      });

      this.noteReminders = (response?.data?.NoteReminders ?? []) as Model.NoteReminder[];
    } catch (err) {
      console.log("loadNotes -> error:", err);
      this.errorText = `Error downloading notes: ${err}`;
    } finally {
      this.loading = false;
    }
  }

  async saveNote() {
    if (!this.canSave) return;

    this.saving = true;
    this.errorText = "";

    try {
      const existing = this.selectedNote;
      const noteReminder: Model.NoteReminder = {
        typename: "NoteReminder",
        guid: existing?.guid || `${Date.now()}-${uuidv4()}`,
        metadata: this.$store.getters["user/getUpstreamMetadata"](),
        changeTracking: new Model.ChangeTrackComponent(),
        time: this.selectedDateValue,
        system: NOTE_SYSTEM,
        category: this.selectedCategory || this.defaultCategory(),
        remind: this.remind,
        note: this.noteText.trim(),
      };

      await this.$store.dispatch("graphQl", {
        gql: `mutation noteReminder($guid: String!, $noteReminder: NoteReminderInput!) {
          noteReminder(guid: $guid, noteReminder: $noteReminder)
        }`,
        variables: {
          guid: this.$store.state.user.location.guid,
          noteReminder,
        },
      });

      await this.loadNotes();
      this.selectedNoteGuid = noteReminder.guid;
      this.syncEditorFromSelectedNote();
      this.snackText = "Note saved";
      this.snack = true;
    } catch (err) {
      console.log("saveNote -> error:", err);
      this.errorText = `Error saving note: ${err}`;
    } finally {
      this.saving = false;
    }
  }

  async deleteNote() {
    const note = this.noteToDelete;
    if (!note || this.deleting) return;

    this.deleting = true;
    this.errorText = "";

    try {
      await this.$store.dispatch("graphQl", {
        gql: `mutation removeNoteReminder($guid: String!, $noteReminderGuid: String!) {
          removeNoteReminder(guid: $guid, noteReminderGuid: $noteReminderGuid)
        }`,
        variables: {
          guid: this.$store.state.user.location.guid,
          noteReminderGuid: note.guid,
        },
      });

      await this.loadNotes();
      if (this.selectedNoteGuid === note.guid) this.resetEditor();
      this.deleteDialog = false;
      this.noteToDelete = null;
      this.snackText = "Note deleted";
      this.snack = true;
    } catch (err) {
      console.log("deleteNote -> error:", err);
      this.errorText = `Error deleting note: ${err}`;
    } finally {
      this.deleting = false;
    }
  }
}
